/**
 * Global OAuth Sync Coordinator
 * Prevents duplicate OAuth syncs across multiple component instances
 */

interface SyncOperation {
  userId: string;
  promise: Promise<boolean>;
  timestamp: number;
}

class GoogleOAuthCoordinator {
  private activeSyncs: Map<string, SyncOperation> = new Map();
  private lastSuccessfulSync: Map<string, number> = new Map();
  private readonly COOLDOWN_MS = 5000; // 5 seconds cooldown after successful sync

  /**
   * Check if a sync is currently running for this user
   */
  isSyncing(userId: string): boolean {
    return this.activeSyncs.has(userId);
  }

  /**
   * Check if we're in cooldown period after a recent sync
   */
  isInCooldown(userId: string): boolean {
    const lastSync = this.lastSuccessfulSync.get(userId);
    if (!lastSync) return false;
    
    const elapsed = Date.now() - lastSync;
    return elapsed < this.COOLDOWN_MS;
  }

  /**
   * Get or create a sync operation for this user
   * Returns the active sync promise if one exists, or creates a new one
   */
  async coordinateSync(
    userId: string,
    syncFunction: () => Promise<boolean>
  ): Promise<boolean> {
    // Check cooldown first
    if (this.isInCooldown(userId)) {
      console.log('⏸️ OAuth Coordinator: In cooldown period, skipping sync');
      return true; // Return success since we just synced
    }

    // Check if sync is already in progress
    const existingSync = this.activeSyncs.get(userId);
    if (existingSync) {
      console.log('🔄 OAuth Coordinator: Sync already in progress, waiting for result...');
      return existingSync.promise;
    }

    // Start new sync operation
    console.log('▶️ OAuth Coordinator: Starting new sync for user:', userId);
    const syncPromise = syncFunction()
      .then((result) => {
        if (result) {
          this.lastSuccessfulSync.set(userId, Date.now());
          console.log('✅ OAuth Coordinator: Sync completed successfully');
        } else {
          console.log('❌ OAuth Coordinator: Sync failed');
        }
        return result;
      })
      .finally(() => {
        // Clean up active sync
        this.activeSyncs.delete(userId);
        console.log('🧹 OAuth Coordinator: Cleaned up sync operation');
      });

    // Store the active sync
    this.activeSyncs.set(userId, {
      userId,
      promise: syncPromise,
      timestamp: Date.now(),
    });

    return syncPromise;
  }

  /**
   * Clear cooldown for a user (useful for manual retry)
   */
  clearCooldown(userId: string): void {
    this.lastSuccessfulSync.delete(userId);
    console.log('🔓 OAuth Coordinator: Cooldown cleared for user');
  }

  /**
   * Get time remaining in cooldown (in ms)
   */
  getCooldownRemaining(userId: string): number {
    const lastSync = this.lastSuccessfulSync.get(userId);
    if (!lastSync) return 0;
    
    const elapsed = Date.now() - lastSync;
    return Math.max(0, this.COOLDOWN_MS - elapsed);
  }
}

// Singleton instance
export const googleOAuthCoordinator = new GoogleOAuthCoordinator();
